import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { PlatformName, PlatformStat } from "../mockAnalytics";

interface PlatformBreakdownTableProps {
  data: PlatformStat[];
}

const PLATFORM_DOT: Record<PlatformName, string> = {
  Messenger: "bg-channel-messenger",
  Instagram: "bg-channel-instagram",
  WhatsApp: "bg-channel-whatsapp",
  Website: "bg-channel-website",
};

const PLATFORM_CHIP: Record<PlatformName, string> = {
  Messenger: "bg-channel-messenger/10 text-channel-messenger border-channel-messenger/20",
  Instagram: "bg-channel-instagram/10 text-channel-instagram border-channel-instagram/20",
  WhatsApp: "bg-channel-whatsapp/10 text-channel-whatsapp border-channel-whatsapp/20",
  Website: "bg-channel-website/10 text-channel-website border-channel-website/20",
};

/** Per-channel performance table: messages, leads, conversion, response time, CSAT. */
export function PlatformBreakdownTable({ data }: PlatformBreakdownTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[560px] border-collapse text-sm">
        <thead>
          <tr className="border-b border-slate-200/80 dark:border-slate-800 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            <th className="py-2.5 pr-3">Channel</th>
            <th className="px-3 py-2.5 text-right">Messages</th>
            <th className="px-3 py-2.5 text-right">Leads</th>
            <th className="px-3 py-2.5 text-right">Conv. Rate</th>
            <th className="px-3 py-2.5 text-right">Avg Response</th>
            <th className="py-2.5 pl-3 text-right">CSAT</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={row.platform}
              className="border-b border-slate-100 dark:border-slate-800/60 transition-colors last:border-0 hover:bg-slate-50/80 dark:hover:bg-slate-800/40"
            >
              <td className="py-2.5 pr-3">
                <Badge
                  variant="outline"
                  className={cn("gap-1.5 font-semibold text-xs py-0.5", PLATFORM_CHIP[row.platform])}
                >
                  <span className={cn("h-1.5 w-1.5 rounded-full", PLATFORM_DOT[row.platform])} />
                  {row.platform}
                </Badge>
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 dark:text-slate-300">
                {row.messages.toLocaleString()}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 dark:text-slate-300">
                {row.leads.toLocaleString()}
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums font-bold text-slate-900 dark:text-white">
                {row.convRate.toFixed(1)}%
              </td>
              <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 dark:text-slate-300">
                {row.avgResponseSeconds.toFixed(1)}s
              </td>
              <td className="py-2.5 pl-3 text-right tabular-nums font-bold text-slate-900 dark:text-white">
                {row.csat.toFixed(1)}/5
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
